import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useActiveBloodRequests } from '../hooks/useSupabase';
import { isDonorEligible } from '../types/schema';

/* ─── Animated counter ─────────────────────────────────────── */
const CountUp = ({ target, suffix = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(target / 40);
    const timer = setInterval(() => {
      start = Math.min(start + step, target);
      if (ref.current) ref.current.textContent = start + suffix;
      if (start >= target) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, [target, suffix]);
  return <span ref={ref}>0{suffix}</span>;
};

/* ─── Blood type badge ─────────────────────────────────────── */
const BloodTypePill = ({ type }) => (
  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl text-sm font-black text-crimson-700"
    style={{ background: 'linear-gradient(135deg,#fff1f2,#fecdd3)', border: '1.5px solid #fecdd3' }}>
    {type}
  </span>
);

/* ─── Urgency badge ────────────────────────────────────────── */
const UrgencyBadge = ({ level }) => {
  const map = {
    critical: { cls: 'badge-critical', label: '🔴 Critical' },
    high:     { cls: 'badge-high',     label: '🟠 High' },
    medium:   { cls: 'badge-medium',   label: '🟡 Medium' },
    low:      { cls: 'badge-low',      label: '🔵 Low' },
  };
  const { cls, label } = map[level] || map.low;
  return <span className={cls}>{label}</span>;
};

/* ─── Logged-in Donor Dashboard ────────────────────────────── */
const DonorDashboard = ({ currentDonor }) => {
  const { data: allRequests, loading } = useActiveBloodRequests();
  const isEligible = isDonorEligible(currentDonor);

  const critical = allRequests?.filter(r => r.urgencyLevel === 'critical') || [];
  const high     = allRequests?.filter(r => r.urgencyLevel === 'high') || [];
  const others   = allRequests?.filter(r => r.urgencyLevel !== 'critical' && r.urgencyLevel !== 'high') || [];
  const sorted   = [...critical, ...high, ...others];

  return (
    <div className="page-shell min-h-screen" style={{ background: '#f9efd5' }}>
      {/* ── Welcome banner ── */}
      <section className="pt-6 pb-2 section-container">
        <div className={`relative overflow-hidden rounded-2xl p-5 md:p-7 ${
          isEligible
            ? 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'
            : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'
        }`}>
          <div className={`absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-20 ${isEligible ? 'bg-green-400' : 'bg-amber-400'}`} />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${isEligible ? 'bg-green-200' : 'bg-amber-200'}`}>
                {isEligible ? '✅' : '⏳'}
              </div>
              <div>
                <h2 className={`text-lg font-bold ${isEligible ? 'text-green-900' : 'text-amber-900'}`}>
                  Welcome back, {currentDonor.name?.split(' ')[0]}!
                </h2>
                <p className={`text-sm mt-0.5 ${isEligible ? 'text-green-700' : 'text-amber-700'}`}>
                  {isEligible
                    ? '🎉 You are eligible to donate — thank you for being a lifesaver!'
                    : `⏰ Not yet eligible to donate.`}
                  {!isEligible && currentDonor.next_eligible_date && (
                    <span className="font-medium"> Eligible from {new Date(currentDonor.next_eligible_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}.</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to="/profile" className="btn btn-ghost btn-sm border border-surface-200 bg-white hover:bg-surface-50 whitespace-nowrap">
                👤 My Profile
              </Link>
              <Link to="/request-blood" className="btn btn-sm whitespace-nowrap" style={{ background: '#c4451a', color: 'white' }}>
                🆘 Request Blood
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Active blood requests ── */}
      <section className="section-container py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-surface-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              🩸 Active Blood Requests
            </h2>
            {!loading && (
              <p className="text-sm text-surface-500 mt-0.5">
                {sorted.length === 0 ? 'No active requests right now' : `${sorted.length} request${sorted.length > 1 ? 's' : ''} need help`}
              </p>
            )}
          </div>
          <Link to="/requests" className="btn btn-ghost btn-sm text-crimson-600 hover:bg-crimson-50 whitespace-nowrap">
            View All →
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="skeleton h-36 rounded-2xl" />)}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16 card">
            <div className="text-5xl mb-4">🎉</div>
            <p className="text-surface-600 font-medium">No active blood requests right now.</p>
            <p className="text-surface-400 text-sm mt-1">Check back later or spread the word!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sorted.map(request => (
              <Link
                key={request.id}
                to={`/requests/${request.id}`}
                className="card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group border-l-4 block cursor-pointer"
                style={{ borderLeftColor: request.urgencyLevel === 'critical' ? '#dc2626' : request.urgencyLevel === 'high' ? '#ea580c' : '#ca8a04' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-surface-800 truncate mb-1 group-hover:text-crimson-700 transition-colors">{request.patientName}</h3>
                    <p className="text-xs text-surface-500 truncate">📍 {request.hospital?.name || request.hospital}, {request.hospital?.district || request.district}</p>
                  </div>
                  <BloodTypePill type={request.bloodGroup} />
                </div>
                <div className="flex items-center justify-between">
                  <UrgencyBadge level={request.urgencyLevel} />
                  <span className="text-xs text-surface-400">{request.unitsRequired} unit{request.unitsRequired > 1 ? 's' : ''}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-surface-100 flex items-center justify-between">
                  <span className="text-xs font-medium text-crimson-600">Tap to help →</span>
                  {request.requiredByDate && (
                    <span className="text-xs text-surface-400">
                      By {new Date(request.requiredByDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ── Quick links ── */}
      <section className="section-container pb-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: '/requests', icon: '🩸', label: 'All Requests', color: 'bg-red-50 border-red-100 text-red-700' },
            { to: '/stars', icon: '🏆', label: 'Leaderboard', color: 'bg-amber-50 border-amber-100 text-amber-700' },
            { to: '/request-blood', icon: '🆘', label: 'Need Blood?', color: 'bg-orange-50 border-orange-100 text-orange-700' },
            { to: '/profile', icon: '👤', label: 'My Profile', color: 'bg-blue-50 border-blue-100 text-blue-700' },
            { to: '/donor-registration', icon: '❤️', label: 'Donate Blood', color: 'bg-crimson-50 border-crimson-100 text-crimson-700' },
          ].map(({ to, icon, label, color }) => (
            <Link key={to} to={to} className={`flex flex-col items-center gap-2 p-4 rounded-2xl border font-medium text-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${color}`}>
              <span className="text-2xl">{icon}</span>
              {label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

/* ─── Admin Home Dashboard ─────────────────────────────────── */
const AdminHomeDashboard = ({ adminUser }) => {
  const { data: allRequests, loading } = useActiveBloodRequests();
  const critical = allRequests?.filter(r => r.urgencyLevel === 'critical') || [];
  const high     = allRequests?.filter(r => r.urgencyLevel === 'high') || [];
  const others   = allRequests?.filter(r => r.urgencyLevel !== 'critical' && r.urgencyLevel !== 'high') || [];
  const sorted   = [...critical, ...high, ...others];

  return (
    <div className="page-shell min-h-screen" style={{ background: '#f9efd5' }}>
      {/* Welcome banner */}
      <section className="pt-6 pb-2 section-container">
        <div className="relative overflow-hidden rounded-2xl p-5 md:p-7 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-20 bg-purple-400" />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 bg-purple-200">🔑</div>
              <div>
                <h2 className="text-lg font-bold text-purple-900">Welcome, {adminUser.name || adminUser.email?.split('@')[0]}!</h2>
                <p className="text-sm mt-0.5 text-purple-700">{adminUser.role?.replace('_', ' ')} · {adminUser.allowed_locations?.length ? adminUser.allowed_locations.join(', ') : 'All Districts'}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to="/admin" className="btn btn-sm whitespace-nowrap" style={{ background: '#c4451a', color: 'white' }}>⚙️ Admin Panel</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Active blood requests */}
      <section className="section-container py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-surface-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>🩸 Active Blood Requests</h2>
            {!loading && <p className="text-sm text-surface-500 mt-0.5">{sorted.length === 0 ? 'No active requests' : `${sorted.length} request${sorted.length > 1 ? 's' : ''} need attention`}</p>}
          </div>
          <Link to="/requests" className="btn btn-ghost btn-sm text-crimson-600 hover:bg-crimson-50 whitespace-nowrap">View All →</Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="skeleton h-36 rounded-2xl" />)}
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16 card"><div className="text-5xl mb-4">🎉</div><p className="text-surface-600 font-medium">No active requests right now.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sorted.map(request => (
              <Link key={request.id} to={`/requests/${request.id}`}
                className="card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group border-l-4 block cursor-pointer"
                style={{ borderLeftColor: request.urgencyLevel === 'critical' ? '#dc2626' : request.urgencyLevel === 'high' ? '#ea580c' : '#ca8a04' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-surface-800 truncate mb-1 group-hover:text-crimson-700 transition-colors">{request.patientName}</h3>
                    <p className="text-xs text-surface-500 truncate">📍 {request.hospital?.name || request.hospital}, {request.hospital?.district || request.district}</p>
                  </div>
                  <BloodTypePill type={request.bloodGroup} />
                </div>
                <div className="flex items-center justify-between">
                  <UrgencyBadge level={request.urgencyLevel} />
                  <span className="text-xs text-surface-400">{request.unitsRequired} unit{request.unitsRequired > 1 ? 's' : ''}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-surface-100 flex items-center justify-between">
                  <span className="text-xs font-medium text-crimson-600">View Details →</span>
                  {request.requiredByDate && <span className="text-xs text-surface-400">By {new Date(request.requiredByDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Admin quick links */}
      <section className="section-container pb-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { to: '/admin', icon: '⚙️', label: 'Admin Panel', color: 'bg-purple-50 border-purple-100 text-purple-700' },
            { to: '/requests', icon: '🩸', label: 'All Requests', color: 'bg-red-50 border-red-100 text-red-700' },
            { to: '/stars', icon: '🏆', label: 'Leaderboard', color: 'bg-amber-50 border-amber-100 text-amber-700' },
            { to: '/request-blood', icon: '🆘', label: 'New Request', color: 'bg-orange-50 border-orange-100 text-orange-700' },
          ].map(({ to, icon, label, color }) => (
            <Link key={to} to={to} className={`flex flex-col items-center gap-2 p-4 rounded-2xl border font-medium text-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${color}`}>
              <span className="text-2xl">{icon}</span>
              {label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

/* ─── Main Component ───────────────────────────────────────── */
const HomePage = ({ currentDonor, adminUser }) => {
  const { data: recentRequests, loading } = useActiveBloodRequests();
  const urgentRequests = recentRequests
    ?.filter(r => r.urgencyLevel === 'critical' || r.urgencyLevel === 'high')
    .slice(0, 3) || [];

  const isEligible = currentDonor ? isDonorEligible(currentDonor) : false;

  // Admin → show admin home dashboard
  if (adminUser) return <AdminHomeDashboard adminUser={adminUser} />;

  // Logged-in donor → show personalised dashboard
  if (currentDonor) return <DonorDashboard currentDonor={currentDonor} />;

  return (
    <div className="page-shell -mt-16">

      {/* ══════════════════════════════════════════════════════
          HERO SECTION
          ════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden pt-16 min-h-[90vh] flex flex-col justify-center"
        style={{ background: 'linear-gradient(135deg, #D4A017 0%, #C49010 25%, #B8860B 55%, #A07520 75%, #8B6914 100%)' }}>
        {/* Subtle texture overlay */}
        <div className="absolute inset-0 bg-dot-pattern opacity-10" />
        {/* Radial glow – warm highlight */}
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full opacity-25"
          style={{ background: 'radial-gradient(circle, rgba(255,220,80,0.7) 0%, transparent 65%)' }} />
        {/* Dark vignette corners */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(100,60,0,0.35) 100%)' }} />

        <div className="relative section-container py-20 md:py-28">
          <div className="max-w-4xl mx-auto text-center">

            {/* Pill label */}
            <div className="inline-flex items-center gap-2 mb-6 animate-fadeInUp"
              style={{ background: 'rgba(0,0,0,0.18)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '9999px', padding: '0.5rem 1rem', color: 'white', fontSize: '0.875rem', fontWeight: 600 }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#fff3c4' }}></span>
              OCYM Raktadhan · रक्तदान · Kerala
            </div>

            {/* Main heading */}
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-3 leading-[1.08] animate-fadeInUp delay-100"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.03em', color: 'white', textShadow: '0 2px 16px rgba(0,0,0,0.4)' }}>
              Every Drop
              <br />
              <span style={{ color: '#fff3c4', textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
                Saves a Life.
              </span>
            </h1>

            {/* Hindi branding */}
            <div className="mb-6 animate-fadeInUp delay-150">
              <span
                className="inline-block text-2xl sm:text-3xl md:text-4xl font-bold tracking-wide"
                style={{ fontFamily: 'Noto Sans Devanagari, serif', color: 'rgba(255,243,196,0.9)', textShadow: '0 2px 12px rgba(0,0,0,0.4)', letterSpacing: '0.04em' }}
              >
                रक्तदान
              </span>
              <span className="ml-3 text-sm font-medium align-middle" style={{ color: 'rgba(255,255,255,0.5)' }}>Blood Donation</span>
            </div>

            {/* Subheading */}
            <p className="text-lg sm:text-xl md:text-2xl max-w-2xl mx-auto mb-10 leading-relaxed animate-fadeInUp delay-200"
              style={{ color: 'rgba(255,255,255,0.88)', textShadow: '0 1px 6px rgba(0,0,0,0.3)' }}>
              Connecting blood donors with people in urgent need across Kerala. 
              One donation can save up to <strong style={{ color: 'white' }}>three lives</strong>.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeInUp delay-300">
              {!currentDonor ? (
                <>
                  <Link
                    to="/donor-registration"
                    className="btn btn-xl rounded-2xl font-bold transition-all shadow-2xl hover:-translate-y-1 group"
                    style={{ background: '#3e1f00', color: '#fff3c4', boxShadow: '0 8px 24px rgba(62,31,0,0.4)' }}
                  >
                    <span>❤️</span>
                    Register as Donor
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <Link
                    to="/request-blood"
                    className="btn btn-xl rounded-2xl font-semibold transition-all"
                    style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(12px)', border: '2px solid rgba(255,255,255,0.4)', color: '#3e1f00' }}
                  >
                    🆘 Request Blood
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/requests"
                    className="btn btn-xl rounded-2xl font-bold transition-all shadow-2xl hover:-translate-y-1 group"
                    style={{ background: '#3e1f00', color: '#fff3c4', boxShadow: '0 8px 24px rgba(62,31,0,0.4)' }}
                  >
                    <span>🩸</span>
                    View Blood Requests
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <Link
                    to="/request-blood"
                    className="btn btn-xl rounded-2xl font-semibold transition-all"
                    style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(12px)', border: '2px solid rgba(255,255,255,0.4)', color: '#3e1f00' }}
                  >
                    🆘 Request Blood
                  </Link>
                </>
              )}
            </div>

            {/* Stats row */}
            <div className="mt-16 grid grid-cols-3 gap-4 max-w-2xl mx-auto animate-fadeInUp delay-300">
              {[
                { value: recentRequests?.length || 0, label: 'Active Requests', suffix: '' },
                { value: 14, label: 'Districts Covered', suffix: '' },
                { value: 24, label: 'Hours Support', suffix: '/7' },
              ].map(({ value, label, suffix }) => (
                <div key={label} className="text-center py-5 px-4 rounded-2xl"
                  style={{ background: 'rgba(0,0,0,0.22)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.25)' }}>
                  <div className="text-3xl md:text-4xl font-black mb-1" style={{ color: 'white', textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}>
                    <CountUp target={value} suffix={suffix} />
                  </div>
                  <div className="text-xs md:text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 80L48 72C96 64 192 48 288 44C384 40 480 48 576 52C672 56 768 56 864 52C960 48 1056 40 1152 38C1248 36 1344 40 1392 42L1440 44V80H1392C1344 80 1248 80 1152 80C1056 80 960 80 864 80C768 80 672 80 576 80C480 80 384 80 288 80C192 80 96 80 48 80H0Z"
              fill="#f9efd5"/>
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          DONOR STATUS BANNER
          ════════════════════════════════════════════════════ */}
      {currentDonor && (
        <section className="section-container pt-8 pb-0">
          <div className={`relative overflow-hidden rounded-2xl p-6 md:p-8 ${
            isEligible
              ? 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'
              : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'
          }`}>
            {/* Decorative circle */}
            <div className={`absolute -right-6 -top-6 w-32 h-32 rounded-full opacity-20 ${
              isEligible ? 'bg-green-400' : 'bg-amber-400'
            }`} />
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 relative">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shrink-0 ${
                  isEligible ? 'bg-green-200' : 'bg-amber-200'
                }`}>
                  {isEligible ? '✅' : '⏳'}
                </div>
                <div>
                  <h2 className={`text-xl font-bold mb-1 ${isEligible ? 'text-green-900' : 'text-amber-900'}`}>
                    Welcome back, {currentDonor.name?.split(' ')[0]}!
                  </h2>
                  <p className={`text-sm ${isEligible ? 'text-green-700' : 'text-amber-700'}`}>
                    {isEligible
                      ? '🎉 You are eligible to donate. Thank you for being a lifesaver!'
                      : '⏰ You are currently not eligible to donate. Check your profile for details.'}
                  </p>
                </div>
              </div>
              <div className="flex gap-3 flex-wrap">
                <Link to="/profile" className="btn btn-ghost btn-sm border border-surface-200 bg-white hover:bg-surface-50">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  View Profile
                </Link>
                {isEligible && (
                  <Link to="/requests" className="btn btn-primary btn-sm">
                    🩸 Find Requests
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          URGENT REQUESTS
          ════════════════════════════════════════════════════ */}
      {(loading || urgentRequests.length > 0) && (
        <section className="section-container py-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-surface-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                🩸 Pending Blood Requests
              </h2>
              <p className="text-surface-500 text-sm mt-1">Click on a request to view details</p>
            </div>
            <Link to="/requests" className="btn btn-ghost btn-sm text-crimson-600 hover:text-crimson-700 hover:bg-crimson-50">
              View All
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1,2,3].map(i => (
                <div key={i} className="skeleton h-32 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {urgentRequests.map((request) => (
                <Link
                  key={request.id}
                  to={`/requests/${request.id}`}
                  className="card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group border-l-4 border-l-crimson-500 block cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-surface-800 truncate mb-1 group-hover:text-crimson-700 transition-colors">{request.patientName}</h3>
                      <p className="text-xs text-surface-500">📍 {request.hospital?.name}, {request.hospital?.district}</p>
                    </div>
                    <BloodTypePill type={request.bloodGroup} />
                  </div>
                  <div className="flex items-center justify-between">
                    <UrgencyBadge level={request.urgencyLevel} />
                    <span className="text-xs text-surface-400">{request.unitsRequired} unit{request.unitsRequired > 1 ? 's' : ''}</span>
                  </div>
                  <div className="mt-4 pt-3 border-t border-surface-100 flex items-center justify-between">
                    <span className="text-xs font-medium text-crimson-600">View Details →</span>
                    <span className="text-xs text-surface-400">Tap to help</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS
          ════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <div className="section-header">
            <span className="badge-crimson mb-4 inline-flex px-4 py-1.5 text-xs font-bold uppercase tracking-widest">How it Works</span>
            <h2 className="section-title">Simple steps to save lives</h2>
            <p className="section-subtitle">Donate or connect with donors in just a few clicks</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 relative">
            {/* Connecting lines (desktop) */}
            <div className="hidden md:block absolute top-12 left-[33%] right-[33%] h-0.5 bg-gradient-to-r from-crimson-200 to-crimson-200" 
              style={{ borderTop: '2px dashed #fecdd3' }} />

            {[
              {
                step: '01',
                icon: '👤',
                title: 'Register as Donor',
                desc: 'Create your profile with blood group, location, and contact info. Takes less than 2 minutes.',
                color: 'from-crimson-50 to-rose-50',
                iconBg: 'from-crimson-100 to-rose-100',
              },
              {
                step: '02',
                icon: '🔍',
                title: 'Browse Requests',
                desc: 'See real-time blood requests in your area. Filter by district and blood group for quick matches.',
                color: 'from-orange-50 to-amber-50',
                iconBg: 'from-orange-100 to-amber-100',
              },
              {
                step: '03',
                icon: '🩸',
                title: 'Donate & Save Lives',
                desc: 'Connect with the requester, coordinate the donation, and earn recognition on our leaderboard.',
                color: 'from-green-50 to-emerald-50',
                iconBg: 'from-green-100 to-emerald-100',
              },
            ].map(({ step, icon, title, desc, color, iconBg }) => (
              <div key={step} className={`step-card bg-gradient-to-br ${color}`}>
                <div className={`step-icon bg-gradient-to-br ${iconBg} text-3xl`}>
                  {icon}
                </div>
                <span className="text-xs font-black text-surface-400 tracking-widest mb-2 block">STEP {step}</span>
                <h3 className="text-lg font-bold text-surface-800 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</h3>
                <p className="text-surface-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/donor-registration" className="btn btn-primary btn-lg rounded-xl">
              ❤️ Start Saving Lives
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          STATS SECTION
          ════════════════════════════════════════════════════ */}
      <section className="py-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #D4A017 0%, #C49010 30%, #B8860B 60%, #8B6914 100%)' }}>
        <div className="absolute inset-0 bg-dot-pattern opacity-10" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(100,60,0,0.3) 100%)' }} />

        <div className="relative section-container">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-black mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em', color: 'white', textShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>
              Making a Real Difference
            </h2>
            <p className="text-lg" style={{ color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 6px rgba(0,0,0,0.25)' }}>Facts that matter when every second counts</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '1', label: 'Donation', sub: 'saves 3 lives', icon: '❤️' },
              { value: '56', label: 'Days', sub: 'between donations', icon: '📅' },
              { value: '45', label: 'Kg min', sub: 'weight required', icon: '⚖️' },
              { value: '18-65', label: 'Age Range', sub: 'for donation', icon: '🧬' },
            ].map(({ value, label, sub, icon }) => (
              <div key={label} className="text-center py-6 px-4 rounded-2xl"
                style={{ background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <div className="text-3xl mb-2">{icon}</div>
                <div className="text-3xl md:text-4xl font-black mb-1" style={{ color: 'white', textShadow: '0 1px 8px rgba(0,0,0,0.3)' }}>{value}</div>
                <div className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>{label}</div>
                <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          OCYM SECTION
          ════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image / Visual */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-crimson-800 to-crimson-950 aspect-video flex items-center justify-center shadow-2xl">
                <div className="text-center text-white p-12">
                  <div className="mb-6 animate-float flex justify-center">
                    <img
                      src="/ocym-logo.png"
                      alt="OCYM Logo"
                      className="w-28 h-28 object-contain drop-shadow-2xl"
                    />
                  </div>
                  <div className="text-2xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>OCYM</div>
                  <div className="text-crimson-300 text-sm">Orthodox Christian Youth Movement</div>
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 card px-5 py-4 shadow-xl animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-crimson-100 flex items-center justify-center text-xl">🏆</div>
                  <div>
                    <div className="text-sm font-bold text-surface-800">83 Years</div>
                    <div className="text-xs text-surface-500">of Active Service</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="badge-crimson mb-4 inline-flex px-4 py-1.5 text-xs font-bold uppercase tracking-widest">Our Initiative</span>
              <h2 className="text-3xl md:text-4xl font-bold text-surface-900 mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em' }}>
                An Initiative by OCYM
              </h2>
              <p className="text-surface-600 text-lg leading-relaxed mb-6">
                The Orthodox Christian Youth Movement (OCYM), the Youth-wing of the Malankara Orthodox Syrian Church, 
                is in its <strong>83rd year</strong> of active leadership and Christian witness. 
                This initiative connects blood donors and recipients across Kerala, one life at a time.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: '🌍', label: '14 Districts', sub: 'across Kerala' },
                  { icon: '📱', label: 'Real-time', sub: 'notifications' },
                  { icon: '🔒', label: 'Secure', sub: 'data privacy' },
                  { icon: '❤️', label: 'Community', sub: 'driven mission' },
                ].map(({ icon, label, sub }) => (
                  <div key={label} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-crimson-50 flex items-center justify-center text-lg shrink-0">
                      {icon}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-surface-800">{label}</div>
                      <div className="text-xs text-surface-500">{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
              <a
                href="https://ocymonline.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline btn-md"
              >
                Learn about OCYM
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          FINAL CTA
          ════════════════════════════════════════════════════ */}
      <section className="py-20 bg-surface-50">
        <div className="section-container">
          <div className="card-elevated max-w-3xl mx-auto p-10 md:p-14 text-center relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-60 h-60 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(244,63,94,0.08) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
            <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(244,63,94,0.05) 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />

            <div className="relative">
              <div className="text-6xl mb-6 animate-float">❤️</div>
              <h2 className="text-3xl md:text-4xl font-black text-surface-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em' }}>
                Ready to Save Lives?
              </h2>
              <p className="text-surface-500 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
                Join hundreds of OCYM donors making a difference in their community. 
                Registration takes less than 2 minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/donor-registration" className="btn btn-primary btn-lg rounded-xl">
                  ❤️ Become a Donor
                </Link>
                <Link to="/requests" className="btn btn-outline btn-lg rounded-xl">
                  🩸 View Requests
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;